/* eslint-disable no-unused-vars */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Table from '../../../../Components/Table';
import axios from 'axios';
import { API_CONSTANTS } from '../../../../API_CONSTANTS';
import Modal from 'react-bootstrap/Modal';
import Toast from '../../../../Components/Toast/Toast';
import toArrayBuffer from 'to-array-buffer';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import moment from 'moment';
import {
    faCheckCircle,
    faTimesCircle,
} from '@fortawesome/fontawesome-free-solid';

export default function LabPartners() {
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState("");
    const [labList, setLabList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pageCount, setPageCount] = useState(0);
    const fetchIdRef = useRef(0);

    const [showAddModal, setShowAddModal] = useState(false);
    const [savingLab, setSavingLab] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreviewError, setImagePreviewError] = useState(false);
    const [startTimeValue, setStartTimeValue] = useState(null);
    const [endTimeValue, setEndTimeValue] = useState(null);
    const [formValues, setFormValues] = useState({
        name: "",
        description: "",
        discount: "",
        startTime: "",
        endTime: "",
        noOfTests: "",
        // url will contain the uploaded image URL
        url: "",
        emailProd: "",
        emailDev: "",
        ccProdText: "",
        ccDevText: "",
        key: "",
    });

    const [formErrors, setFormErrors] = useState({});

    const [ToastMessage, setToastMessage] = useState("");
    const [showToast, setShowToast] = useState("");
    const [ToastIcon, setToastIcon] = useState(faTimesCircle);
    const [iconColor, setIconColor] = useState("");
    const handleToast = (message, icon, toastIconColor, className) => {
        setToastIcon(icon);
        setToastMessage(message);
        setShowToast(`show ${className}`);
        setIconColor(toastIconColor);
        setTimeout(() => setShowToast(""), 2900);
    };

    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            setPageCount(Math.ceil(labList.length / pageSize));
        },
        [labList]
    );

    const parseMultiValue = (text) => {
        if (!text) return [];
        return text
            .split(/[\n,]/g)
            .map((value) => value.trim())
            .filter(Boolean);
    };

    const isValidEmail = (value) => {
        if (!value) return true;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
    };

    const clearFieldError = (field) => {
        setFormErrors((prev) => {
            if (!prev?.[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const validateForm = () => {
        const errors = {};

        if (!formValues.name?.trim()) errors.name = "Lab name is required";
        if (!formValues.key?.trim()) errors.key = "Key is required";
        if (!formValues.discount?.toString().trim()) errors.discount = "Discount is required";
        if (!formValues.startTime?.trim()) errors.startTime = "Start time is required";
        if (!formValues.endTime?.trim()) errors.endTime = "End time is required";
        if (!formValues.noOfTests?.toString().trim()) errors.noOfTests = "No. of tests is required";

        if (!formValues.url?.trim()) errors.url = "Lab image is required";
        if (uploadingImage) errors.url = "Please wait for image upload to finish";

        const discountNumber = Number(formValues.discount);
        if (formValues.discount !== "" && (Number.isNaN(discountNumber) || discountNumber < 0 || discountNumber > 100)) {
            errors.discount = "Discount must be between 0 and 100";
        }

        const testsNumber = Number(formValues.noOfTests);
        if (formValues.noOfTests !== "" && (Number.isNaN(testsNumber) || testsNumber <= 0)) {
            errors.noOfTests = "No. of tests must be greater than 0";
        }

        if (formValues.emailProd && !isValidEmail(formValues.emailProd)) {
            errors.emailProd = "Invalid email";
        }
        if (formValues.emailDev && !isValidEmail(formValues.emailDev)) {
            errors.emailDev = "Invalid email";
        }

        const ccProd = parseMultiValue(formValues.ccProdText);
        const invalidCcProd = ccProd.find((email) => !isValidEmail(email));
        if (invalidCcProd) errors.ccProdText = `Invalid email: ${invalidCcProd}`;

        const ccDev = parseMultiValue(formValues.ccDevText);
        const invalidCcDev = ccDev.find((email) => !isValidEmail(email));
        if (invalidCcDev) errors.ccDevText = `Invalid email: ${invalidCcDev}`;

        return errors;
    };

    const resetForm = () => {
        setFormValues({
            name: "",
            description: "",
            discount: "",
            startTime: "",
            endTime: "",
            noOfTests: "",
            url: "",
            emailProd: "",
            emailDev: "",
            ccProdText: "",
            ccDevText: "",
            key: "",
        });
        setFormErrors({});
        setUploadingImage(false);
        setImagePreviewError(false);
        setStartTimeValue(null);
        setEndTimeValue(null);
    };

    useEffect(() => {
        setImagePreviewError(false);
    }, [formValues.url]);

    const uploadImage = async (file) => {
        const reader = new FileReader();
        reader.readAsBinaryString(file);

        const uploaderId =
            localStorage.getItem("editorId") ||
            "a80422ab-1f3c-4676-8200-fc38a156599f";

        return new Promise((resolve, reject) => {
            reader.onload = async () => {
                try {
                    const fileRes = btoa(reader.result);
                    const bytes = toArrayBuffer(fileRes);

                    const response = await axios.post(
                        `${API_CONSTANTS.BASEURL}admin/image-upload-api/user/${uploaderId}/type/new`,
                        bytes,
                        {
                            headers: API_CONSTANTS.imgheaders(),
                        }
                    );

                    resolve(response?.data?.response?.url || "");
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => reject(new Error("File reading error"));
        });
    };

    const handleLabImageChange = async (event) => {
        const file = event?.target?.files?.[0];
        if (!file) return;

        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
        const maxSizeBytes = 5 * 1024 * 1024;
        if (!allowedTypes.includes(file.type)) {
            handleToast("Only JPG, PNG or WEBP images are allowed", faTimesCircle, "#B00020", "show-danger");
            if (event?.target) event.target.value = "";
            return;
        }
        if (file.size > maxSizeBytes) {
            handleToast("Image is too large (max 5MB)", faTimesCircle, "#B00020", "show-danger");
            if (event?.target) event.target.value = "";
            return;
        }

        setUploadingImage(true);
        clearFieldError("url");
        try {
            const url = await uploadImage(file);
            if (!url) {
                handleToast("Image upload failed", faTimesCircle, "#B00020", "show-danger");
                return;
            }
            setFormValues((prev) => ({ ...prev, url }));
            handleToast("Image uploaded successfully", faCheckCircle, "#009b94", "show-success");
        } catch (error) {
            const msg =
                error?.response?.data?.response?.message ||
                error?.response?.data?.message ||
                error?.message ||
                "Error uploading image";
            handleToast(msg, faTimesCircle, "#B00020", "show-danger");
        } finally {
            setUploadingImage(false);
            if (event?.target) event.target.value = "";
        }
    };

    // all labs data API 
    const lab = () => {
        const url = `${API_CONSTANTS.LABURL}admin/labs`;
        setIsLoading(false);

        axios
            .get(url, {
                headers: API_CONSTANTS.headers(),
            })
            .then((res) => {
                setIsLoading(true);

                const response = res?.data?.response;
                const normalized = Array.isArray(response)
                    ? response
                    : Array.isArray(response?.Items)
                        ? response.Items
                        : [];

                setLabList(normalized);
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    window.location.reload();
                    window.localStorage.clear();
                }
                setIsLoading(true);
                setLabList([]);
            });
    };

    const handleCreateLab = () => {
        const url = `${API_CONSTANTS.LABURL}admin/addlabs`;

        setFormErrors({});
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        const payload = {
            name: formValues.name,
            description: formValues.description,
            discount: formValues.discount === "" ? "" : Number(formValues.discount),
            startTime: formValues.startTime,
            endTime: formValues.endTime,
            noOfTests: formValues.noOfTests === "" ? "" : Number(formValues.noOfTests),
            url: formValues.url,
            emailProd: formValues.emailProd,
            emailDev: formValues.emailDev,
            ccProd: parseMultiValue(formValues.ccProdText),
            ccDev: parseMultiValue(formValues.ccDevText),
            key: formValues.key,
        };

        setSavingLab(true);

        axios
            .post(url, payload, {
                headers: API_CONSTANTS.headers(),
            })
            .then((res) => {
                if (res?.data?.message === "Success") {
                    handleToast(
                        "Lab added successfully",
                        faCheckCircle,
                        "#009b94",
                        "show-success"
                    );
                    setShowAddModal(false);
                    resetForm();
                    lab();
                } else {
                    handleToast(
                        res?.data?.message || "Failed to add lab",
                        faTimesCircle,
                        "#B00020",
                        "show-danger"
                    );
                }
            })
            .catch((error) => {
                handleToast(
                    error?.response?.data?.message || "Failed to add lab",
                    faTimesCircle,
                    "#B00020",
                    "show-danger"
                );
            })
            .finally(() => setSavingLab(false));
    };
    const columns = useMemo(
        () => [
            {
                Header: "No.",
                accessor: (row, index) => index + 1,
            },
            {
                Header: "Lab Name",
                accessor: "name",
            },
            {
                Header: "Discount",
                accessor: row => `${row.discount}%`,
            },
            {
                Header: "No. of Tests",
                accessor: "noOfTests",
            },
            {
                Header: "Action",
                accessor: "key",
                Cell: ({ row }) => (
                    <Link
                        to={`/lab/partners/${row.original.key}`}
                        style={{ borderBottom: "1px solid #5C5C5C" }}
                    >
                        View File
                    </Link>
                )
            },
        ],
        []
    );
    useEffect(() => {
        lab();
    }, []);
    return (
        <>
            <div className="row rounded bg-white m-4">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <h3
                            style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                padding: "18px",
                                margin: 0,
                            }}
                        >
                            All Lab Partners
                        </h3>
                        {!isLoading && (
                            <div className="d-flex justify-content-center">
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    style={{ color: "#24AAA4" }}
                                />
                            </div>
                        )}
                    </div>

                    <button
                        className="add-record btn mx-4"
                        onClick={() => {
                            resetForm();
                            setShowAddModal(true);
                        }}
                    >
                        <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
                        Add Lab
                    </button>
                </div>
                <Table
                    columns={columns}
                    data={labList}
                    routingLink={""}
                    pageCount={pageCount}
                    fetchData={fetchData}
                    // loadMoreList={handleLoadMoreData}
                    lastEvaluatedKey={lastEvaluatedKey}
                    loading={loading}
                    setLoading={setLoading}
                    // FilterForm={<LabFilterForm lab={lab} />}
                    recordDownload={true}
                // onDownloadBtn={onDownloadBtn}
                />
            </div>

            <Modal
                show={showAddModal}
                onHide={() => {
                    if (savingLab || uploadingImage) return;
                    setShowAddModal(false);
                    resetForm();
                }}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 18 }}>Add Lab</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Lab Name</label>
                            <input
                                className={`form-control ${formErrors?.name ? "is-invalid" : ""}`}
                                value={formValues.name}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, name: e.target.value });
                                    clearFieldError("name");
                                }}
                                placeholder="Lab Name"
                                type="text"
                            />
                            {formErrors?.name ? (
                                <div className="invalid-feedback">{formErrors.name}</div>
                            ) : null}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Key</label>
                            <input
                                className={`form-control ${formErrors?.key ? "is-invalid" : ""}`}
                                value={formValues.key}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, key: e.target.value });
                                    clearFieldError("key");
                                }}
                                placeholder="e.g. ABC-LABS"
                                type="text"
                            />
                            {formErrors?.key ? (
                                <div className="invalid-feedback">{formErrors.key}</div>
                            ) : null}
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Description</label>
                            <input
                                className="form-control"
                                value={formValues.description}
                                onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
                                placeholder="Description"
                                type="text"
                            />
                        </div>

                        <div className="col-md-12">
                            <label className="form-label">Image</label>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <div
                                    className="border rounded"
                                    style={{ width: 64, height: 64, overflow: "hidden", background: "#f6f6f6" }}
                                >
                                    {uploadingImage ? (
                                        <div
                                            className="d-flex align-items-center justify-content-center w-100 h-100"
                                            style={{ color: "#888", fontSize: 10 }}
                                        >
                                            Uploading
                                        </div>
                                    ) : formValues.url && !imagePreviewError ? (
                                        <img
                                            src={formValues.url}
                                            alt="lab"
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            onError={() => setImagePreviewError(true)}
                                        />
                                    ) : formValues.url && imagePreviewError ? (
                                        <div
                                            className="d-flex align-items-center justify-content-center w-100 h-100"
                                            style={{ color: "#888", fontSize: 10 }}
                                        >
                                            No preview
                                        </div>
                                    ) : null}
                                </div>

                                <div style={{ minWidth: 260, flexGrow: 1 }}>
                                    <input
                                        type="file"
                                        className={`form-control ${formErrors?.url ? "is-invalid" : ""}`}
                                        accept="image/*"
                                        onChange={handleLabImageChange}
                                        disabled={uploadingImage}
                                    />
                                    {formErrors?.url ? (
                                        <div className="invalid-feedback">{formErrors.url}</div>
                                    ) : null}
                                    <small style={{ color: "#888" }}>
                                        {uploadingImage
                                            ? "Uploading image..."
                                            : formValues.url
                                                ? formValues.url
                                                : "No image uploaded"}
                                    </small>
                                </div>

                                {formValues.url ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            setFormValues((prev) => ({ ...prev, url: "" }));
                                            setFormErrors((prev) => ({ ...prev, url: "Lab image is required" }));
                                        }}
                                        disabled={uploadingImage}
                                    >
                                        Remove
                                    </button>
                                ) : null}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <label className="form-label">Discount (%)</label>
                            <input
                                className={`form-control ${formErrors?.discount ? "is-invalid" : ""}`}
                                value={formValues.discount}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, discount: e.target.value });
                                    clearFieldError("discount");
                                }}
                                placeholder="10"
                                type="number"
                            />
                            {formErrors?.discount ? (
                                <div className="invalid-feedback">{formErrors.discount}</div>
                            ) : null}
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Start Time</label>
                            <TimePicker
                                value={startTimeValue}
                                onChange={(value) => {
                                    setStartTimeValue(value);
                                    const formatted = value ? moment(value?.$d || value).format('HH:mm') : '';
                                    setFormValues((prev) => ({ ...prev, startTime: formatted }));
                                    clearFieldError('startTime');
                                }}
                                label="Select Start Time"
                                minutesStep={5}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        error={Boolean(formErrors?.startTime)}
                                        helperText={formErrors?.startTime || ''}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">End Time</label>
                            <TimePicker
                                value={endTimeValue}
                                onChange={(value) => {
                                    setEndTimeValue(value);
                                    const formatted = value ? moment(value?.$d || value).format('HH:mm') : '';
                                    setFormValues((prev) => ({ ...prev, endTime: formatted }));
                                    clearFieldError('endTime');
                                }}
                                label="Select End Time"
                                minutesStep={5}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        error={Boolean(formErrors?.endTime)}
                                        helperText={formErrors?.endTime || ''}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">No. of Tests</label>
                            <input
                                className={`form-control ${formErrors?.noOfTests ? "is-invalid" : ""}`}
                                value={formValues.noOfTests}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, noOfTests: e.target.value });
                                    clearFieldError("noOfTests");
                                }}
                                placeholder="100"
                                type="number"
                            />
                            {formErrors?.noOfTests ? (
                                <div className="invalid-feedback">{formErrors.noOfTests}</div>
                            ) : null}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Prod Email</label>
                            <input
                                className={`form-control ${formErrors?.emailProd ? "is-invalid" : ""}`}
                                value={formValues.emailProd}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, emailProd: e.target.value });
                                    clearFieldError("emailProd");
                                }}
                                placeholder="prod@example.com"
                                type="email"
                            />
                            {formErrors?.emailProd ? (
                                <div className="invalid-feedback">{formErrors.emailProd}</div>
                            ) : null}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Dev Email</label>
                            <input
                                className={`form-control ${formErrors?.emailDev ? "is-invalid" : ""}`}
                                value={formValues.emailDev}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, emailDev: e.target.value });
                                    clearFieldError("emailDev");
                                }}
                                placeholder="dev@example.com"
                                type="email"
                            />
                            {formErrors?.emailDev ? (
                                <div className="invalid-feedback">{formErrors.emailDev}</div>
                            ) : null}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">CC Prod (comma or newline separated)</label>
                            <textarea
                                className={`form-control ${formErrors?.ccProdText ? "is-invalid" : ""}`}
                                value={formValues.ccProdText}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, ccProdText: e.target.value });
                                    clearFieldError("ccProdText");
                                }}
                                rows={3}
                                placeholder="a@x.com, b@y.com"
                            />
                            {formErrors?.ccProdText ? (
                                <div className="invalid-feedback">{formErrors.ccProdText}</div>
                            ) : null}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">CC Dev (comma or newline separated)</label>
                            <textarea
                                className={`form-control ${formErrors?.ccDevText ? "is-invalid" : ""}`}
                                value={formValues.ccDevText}
                                onChange={(e) => {
                                    setFormValues({ ...formValues, ccDevText: e.target.value });
                                    clearFieldError("ccDevText");
                                }}
                                rows={3}
                                placeholder="a@x.com, b@y.com"
                            />
                            {formErrors?.ccDevText ? (
                                <div className="invalid-feedback">{formErrors.ccDevText}</div>
                            ) : null}
                        </div>
                        </div>
                    </LocalizationProvider>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn"
                        style={{
                            border: "1px solid #79747E",
                            color: "#B00020",
                            background: "none",
                        }}
                        onClick={() => {
                            if (savingLab || uploadingImage) return;
                            setShowAddModal(false);
                            resetForm();
                        }}
                        disabled={savingLab || uploadingImage}
                    >
                        Close
                    </button>
                    <button
                        className="btn"
                        style={{ background: "#009B93", color: "#fff" }}
                        onClick={handleCreateLab}
                        disabled={savingLab || uploadingImage}
                    >
                        {savingLab && (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: "6px" }}
                            />
                        )}
                        {uploadingImage && !savingLab && (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: "6px" }}
                            />
                        )}
                        Save
                    </button>
                </Modal.Footer>
            </Modal>

            <Toast
                cName={showToast}
                icon={ToastIcon}
                color={iconColor}
                text={ToastMessage}
            />
            <style jsx="true">{`
          .add-record {
            background: #009b94 !important;
            color: #fff;
          }
          .btn:focus {
            box-shadow: none !important;
          }
          input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .patient-search-input {
            border-radius: 0;
          }
        `}</style>
        </>
    )
}
